import { computed } from "vue";
import { CHOICE_COLOR_PALETTES } from "@/components/Survey/ResultsV2/choiceColorPalettes";
import { POPULATION_AUTO_COLORS } from "@/lib/chartPalettes";
import { NPS_BUCKETS } from "@/components/Survey/ResultsV2/constants";
import { resolveLocalizedTitle } from "@/composables/useCardData";
import { rankingPositions } from "@/composables/useChartRules";

function codesAndLabels(card, locale) {
  if (!card) return [];
  const type = card.type;
  const s = card.specs || {};

  const fromAnswers = (answers) =>
    (answers || []).map((a, i) => ({
      code: String(i + 1),
      label: resolveLocalizedTitle(a.title, locale) || String(i + 1),
    }));
  const fromRange = (min, max) => {
    const out = [];
    for (let n = min; n <= max; n++) out.push({ code: String(n), label: String(n) });
    return out;
  };

  if (type === "q_ranking") return rankingPositions(card);
  if (["q_single", "q_multiple", "q_image_choice", "q_image_choice_multiple", "q_hidden"].includes(type)) {
    return fromAnswers(s.answers);
  }
  if (type === "q_matrix") {
    if (["single", "multiple"].includes(s.matrix_type)) return fromAnswers(s.answers);
    if (s.matrix_type === "rating") return fromRange(s.rating_limits?.min ?? 1, s.rating_limits?.max ?? 10);
    if (s.matrix_type === "nps") return fromRange(0, 10);
    return [];
  }
  if (type === "q_rating") return fromRange(s.rating_limits?.min ?? 1, s.rating_limits?.max ?? 10);
  if (type === "q_slider") return fromRange(s.slider_limits?.min ?? 0, s.slider_limits?.max ?? 10);
  if (type === "q_nps") return fromRange(0, 10);
  return [];
}

export function useChoiceColors(cardMeta, localOptions, updateLocalOption, locale, npsChartType) {
  const entries = computed(() => {
    if (npsChartType?.value) return NPS_BUCKETS.map(({ code, label }) => ({ code, label }));
    return codesAndLabels(cardMeta.value, locale?.value);
  });

  const choiceCodes = computed(() => entries.value.map((e) => e.code));

  const choiceLabelsMap = computed(() => {
    const map = {};
    for (const { code, label } of entries.value) map[code] = label;
    return map;
  });

  const hasChoiceColors = computed({
    get: () => !!localOptions.value.choice_colors,
    set: (val) => {
      if (val) {
        updateLocalOption("choice_colors", {});
      } else {
        updateLocalOption("choice_colors", null);
        selectedPalette.value = null;
      }
    },
  });

  const choiceColors = computed({
    get: () => localOptions.value.choice_colors || {},
    set: (val) => updateLocalOption("choice_colors", val),
  });

  const paletteOptions = [
    { value: null, label: "Custom" },
    ...CHOICE_COLOR_PALETTES.map((p) => ({ value: p.id, label: p.label })),
  ];

  const selectedPalette = computed({
    get: () => localOptions.value.choice_color_palette || null,
    set: (val) => {
      updateLocalOption("choice_color_palette", val);
      if (val) {
        const palette = CHOICE_COLOR_PALETTES.find((p) => p.id === val);
        if (palette) {
          updateLocalOption("choice_colors", palette.generate(choiceCodes.value));
        }
      }
    },
  });

  const defaultChoiceColors = computed(() => {
    if (npsChartType?.value) return Object.fromEntries(NPS_BUCKETS.map((b) => [b.code, b.color]));
    return {};
  });

  const defaultPopColor = computed(() => POPULATION_AUTO_COLORS[0]);

  function updateSingleChoiceColor(code, color) {
    updateLocalOption("choice_colors", { ...choiceColors.value, [code]: color });
    if (selectedPalette.value) {
      updateLocalOption("choice_color_palette", null);
    }
  }

  return {
    choiceCodes,
    choiceLabelsMap,
    hasChoiceColors,
    choiceColors,
    paletteOptions,
    selectedPalette,
    defaultChoiceColors,
    defaultPopColor,
    updateSingleChoiceColor,
  };
}
